(function () {
  'use strict';

  /* ============================================================
     Aurum Brew — Catalogue grouping
     assets/aurum-catalog.js

     Reorders Dawn's product grid into Coffee / Apparel / Drinkware / Bags
     and inserts a heading before each group.

     It moves existing cards. It does not build any. Dawn's cards carry the
     price, the quick-add button, the sold-out state and the image srcset,
     and a hand-rolled replacement would have to track all of that through
     every theme update. Moving nodes keeps every one of those behaviours
     for free.

     The categories come from snippets/aurum-catalog-chrome.liquid as a
     handle→category map built from real product data. Nothing here guesses
     from a title.
     ============================================================ */

  var ORDER = ['coffee', 'apparel', 'drink', 'bags', 'other'];

  function handleOf(card) {
    var a = card.querySelector('a[href*="/products/"]');
    if (!a) return null;
    var m = a.getAttribute('href').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  function build() {
    var host = document.getElementById('acat-host');
    if (!host || host.dataset.acatDone) return;

    var grid = document.getElementById('product-grid') ||
               document.querySelector('.product-grid');
    if (!grid) return;

    var cards = Array.prototype.slice.call(grid.children).filter(function (li) {
      return li.querySelector && li.querySelector('a[href*="/products/"]');
    });
    /* Below three products there is nothing to organise, and a page of
       headings with one card under each looks worse than a plain grid. */
    if (cards.length < 3) return;

    var map;
    try { map = JSON.parse(host.getAttribute('data-map') || '{}'); }
    catch (e) { return; }               /* bad map: leave the grid alone */

    /* Bucket the cards, preserving the order Shopify sorted them in within
       each group — the customer's chosen sort still applies inside a
       section, which it would not if this re-sorted alphabetically. */
    var buckets = {};
    cards.forEach(function (li) {
      var h = handleOf(li);
      var cat = (h && map[h]) || 'other';
      (buckets[cat] = buckets[cat] || []).push(li);
    });

    /* A category with nothing in it must not print a heading. */
    var groups = ORDER.filter(function (c) { return buckets[c] && buckets[c].length; });
    if (groups.length < 2) return;      /* one group is not an organisation */

    var frag = document.createDocumentFragment();
    groups.forEach(function (cat, i) {
      var tpl = host.querySelector('template[data-cat="' + cat + '"]');
      if (tpl) {
        var li = document.createElement('li');
        li.className = 'acat-head' + (i > 0 ? ' acat-head--rule' : '');
        li.setAttribute('role', 'presentation');
        li.appendChild(tpl.content.cloneNode(true));
        frag.appendChild(li);
      }
      buckets[cat].forEach(function (card) { frag.appendChild(card); });
    });

    /* Snapshot before touching anything, so the post-condition below has
       something to put back. */
    var originalOrder = Array.prototype.slice.call(grid.children);

    grid.appendChild(frag);             /* appending a node moves it */

    /* The cards are now in the right order in the DOM, so the CSS `order`
       values the snippet emitted for first paint have done their job. Drop
       them: leaving them on would keep visual order diverging from tab
       order, and would also fight the headings, which have no order value
       and would otherwise all collapse to the front. */
    grid.classList.add('acat-ordered');
    animateHeadings(grid);

    host.dataset.acatDone = '1';
    document.documentElement.classList.add('acat-on');
    retireStaleLabel();

    /* ---- Post-condition ----------------------------------------------
       This file rearranges and hides parts of a live storefront. The first
       version of it hid an ancestor that turned out to contain the product
       grid, and the catalogue went blank in production — every guard I had
       written was about picking the right element, and none was about what
       happened afterwards.

       So: count the products the customer can actually see, and if
       decorating the page has removed any of them, put everything back and
       stand down. An ungrouped catalogue is a small disappointment. An
       empty one is a shop that cannot sell anything. -------------------- */
    if (visibleProducts(grid) < cards.length) {
      rollback(grid, originalOrder);
    }
  }

  /* Counts CARDS the customer can see, not links — each card carries several
     links (image, title, quick-add) and counting those would compare against
     the wrong number entirely. offsetParent is null for anything hidden by
     display:none, whether on the element or on any ancestor, which is
     precisely the failure being guarded against. */
  function visibleProducts(grid) {
    var n = 0;
    var kids = grid.children;
    for (var i = 0; i < kids.length; i++) {
      if (kids[i].classList.contains('acat-head')) continue;
      if (!kids[i].querySelector('a[href*="/products/"]')) continue;
      if (kids[i].offsetParent !== null) n++;
    }
    return n;
  }

  function rollback(grid, originalOrder) {
    document.documentElement.classList.remove('acat-on');
    var heads = grid.querySelectorAll('.acat-head');
    for (var i = 0; i < heads.length; i++) heads[i].remove();
    var hidden = document.querySelectorAll('[data-acat-hidden]');
    for (var j = 0; j < hidden.length; j++) {
      hidden[j].style.display = '';
      hidden[j].removeAttribute('data-acat-hidden');
    }
    for (var k = 0; k < originalOrder.length; k++) grid.appendChild(originalOrder[k]);
    if (window.console) console.warn('[aurum-catalog] products disappeared after grouping — reverted to the plain grid');
  }

  /* ------------------------------------------------------------------
     Draw each heading in as it is reached.

     Deliberately not on load: three of the four sit below the fold, and an
     animation that finishes before anyone scrolls to it has done nothing
     except cost battery.

     Fires once per heading, which is then dropped from the pending list. A
     heading that re-animated every time it crossed the viewport would turn a
     considered detail into a twitch on every scroll. Once the list empties
     the listeners are removed, so nothing stays attached to scroll.

     If the check never runs — no scroll, script error, anything — the
     headings keep their resting state, which the CSS already defines as the
     finished drawing. Nothing is ever left half-drawn.
     ------------------------------------------------------------------ */
  function animateHeadings(grid) {
    var pending = Array.prototype.slice.call(grid.querySelectorAll('.acat-head'));
    if (!pending.length) return;

    /* Deliberately NOT IntersectionObserver.

       IO was the obvious tool and it silently did nothing here. The grid sat
       inside a section carrying content-visibility:auto, and a skipped
       subtree has no layout boxes — so the observer saw empty rects and
       never reported the headings as arriving. Measured on the live page: a
       heading fully in view, 396px tall in a 730px viewport, and a freshly
       attached observer firing not once.

       perf-tune.css now exempts this section, which fixes the cause. This
       uses rects on scroll anyway, because that is true whatever any
       ancestor does with containment, and four elements checked on a
       rAF-throttled scroll is not a measurable cost. A decorative animation
       is not worth a subtle dependency on containment semantics. */
    var ticking = false;

    function check() {
      ticking = false;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      for (var i = pending.length - 1; i >= 0; i--) {
        var el = pending[i];
        var r = el.getBoundingClientRect();
        /* A zero rect means no layout yet — skipped subtree, or images still
           settling. Leave it pending and look again on the next scroll. */
        if (!r.height) continue;
        if (r.top < vh * 0.88 && r.bottom > 0) {
          el.classList.add('is-in');
          pending.splice(i, 1);
        }
      }
      if (!pending.length) teardown();
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      if (window.requestAnimationFrame) window.requestAnimationFrame(check);
      else window.setTimeout(check, 80);
    }

    function teardown() {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    check();                        /* anything already on screen */
    window.setTimeout(check, 400);  /* again once images and layout settle */
    window.setTimeout(check, 1200); /* and once more, for slow-loading cards */
  }

  /* The page carried an "Our Coffees" label above the whole grid, which is
     wrong now and was wrong before — the grid holds tees, mugs and totes
     too.

     The first version of this hid el.closest('.shopify-section'). On this
     store that label lives INSIDE the same section as the product grid, so
     it hid the grid with it and the catalogue went blank. Eight products
     gone, live.

     The lesson is in where the guard was. There was one — an exact text
     match — but it guarded the TRIGGER, not the BLAST RADIUS. Matching the
     right element told me nothing about how much of the page I was about to
     remove. So the check now runs on the thing being hidden: walk up only
     while the candidate contains no product grid and no product links, and
     hide the largest ancestor that is still safe by that test. If even the
     immediate element fails it, nothing is hidden at all. */
  function isSafeToHide(el) {
    return !el.querySelector('#product-grid') &&
           !el.querySelector('.product-grid') &&
           !el.querySelector('a[href*="/products/"]');
  }

  function retireStaleLabel() {
    var main = document.querySelector('main') || document.body;
    var nodes = main.querySelectorAll('h1, h2, h3, p, span, div');

    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.children.length) continue;
      if (el.textContent.trim().toLowerCase() !== 'our coffees') continue;
      if (!isSafeToHide(el)) return;

      /* Climb to tidy away any small decorative wrapper around the label —
         a rule, a spacer — but stop the moment the next parent would take
         products with it. Capped so a flat DOM cannot walk to <body>. */
      var target = el;
      for (var up = 0; up < 4; up++) {
        var p = target.parentElement;
        if (!p || p === main || p === document.body) break;
        if (!isSafeToHide(p)) break;
        target = p;
      }

      target.style.display = 'none';
      target.setAttribute('data-acat-hidden', 'stale label superseded by section headings');
      return;
    }
  }

  /* ------------------------------------------------------------------
     Reveal.

     theme.liquid puts html.acat-wait up before the body parses, so the
     grid is held blank rather than showing Dawn's ungrouped order for the
     moment before this file rearranges it.

     In a finally, not at the end of build(). build() returns early on
     several paths — no host, no grid, fewer than three products, only one
     category, unparseable map — and the grid must appear on every one of
     them. Tying the reveal to success would mean the situations where
     grouping is skipped are also the situations where nothing is visible.
     That is the same mistake that blanked this page once already, just
     with a different trigger.

     rAF is raced against a timer because requestAnimationFrame does not
     run in a background tab: a catalogue opened in a new tab would
     otherwise sit empty until it was looked at. Third time this theme has
     needed that pairing — anchor-glide.js and aurum-policy.js both do.
     ------------------------------------------------------------------ */
  var revealed = false;
  function reveal() {
    if (revealed) return;
    revealed = true;
    document.documentElement.classList.remove('acat-wait');
  }

  function run() {
    try {
      build();
    } finally {
      if (window.requestAnimationFrame) {
        window.requestAnimationFrame(function () {
          window.requestAnimationFrame(reveal);
        });
      }
      window.setTimeout(reveal, 100);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
